import { ChatBubble, Clock, List, Megaphone, Phone, Microphone } from 'iconoir-react'
import type { IvrConfig, IvrNode } from 'app/types/ivr'

type TreeNode = {
  id: string
  type: IvrNode['type']
  label: string
  depth: number
  children: TreeNode[]
}

const NODE_ICONS: Record<IvrNode['type'], typeof ChatBubble> = {
  greeting: ChatBubble,
  menu: List,
  dial: Phone,
  voicemail: Microphone,
  businessHours: Clock,
  announcement: Megaphone,
}

const NODE_LABELS: Record<IvrNode['type'], string> = {
  greeting: `Greeting`,
  menu: `Greeting`,
  dial: `Dial`,
  voicemail: `Voicemail`,
  businessHours: `Business Hours`,
  announcement: `Announcement`,
}

/** Build a tree structure from a flat IvrConfig node map */
function buildTree(config: IvrConfig, nodeId: string, depth: number, visited: Set<string>): TreeNode | undefined {
  if (!nodeId || visited.has(nodeId)) return undefined
  visited.add(nodeId)

  const node = config.nodes[nodeId]
  if (!node) return undefined

  // Root menu shows "Greeting", non-root menus show "Sub-menu"
  const isRootMenu = node.type === `menu` && nodeId === config.root
  const label = (node as any).label || (isRootMenu ? `Greeting` : NODE_LABELS[node.type])
  const children: TreeNode[] = []

  switch (node.type) {
    case `greeting`:
      if (node.next) {
        const child = buildTree(config, node.next, depth + 1, visited)
        if (child) children.push(child)
      }
      break
    case `menu`: {
      let optIdx = 0
      for (const { digit, targetNodeId } of node.options) {
        const child = buildTree(config, targetNodeId, depth + 1, visited)
        if (child) {
          // Sub-menus get "Sub-menu N" label if they don't have a custom label
          if (child.type === `menu` && !(config.nodes[targetNodeId] as any)?.label) {
            child.label = `${digit}. Sub-menu ${digit}`
          } else {
            child.label = `${digit}. ${child.label}`
          }
          children.push(child)
        }
        optIdx++
      }
      break
    }
    case `dial`:
      // noAnswer fallbacks are edited inline in the DialRow component — never show in tree
      break
    case `businessHours`: {
      const visitedDuring = new Set(visited)
      if (node.duringHours) {
        const during = buildTree(config, node.duringHours, depth + 1, visitedDuring)
        if (during) {
          during.label = `Open → ${during.label}`
          children.push(during)
        }
      }
      const visitedAfter = new Set(visited)
      if (node.afterHours) {
        const after = buildTree(config, node.afterHours, depth + 1, visitedAfter)
        if (after) {
          after.label = `Closed → ${after.label}`
          children.push(after)
        }
      }
      break
    }
    case `announcement`:
      if (node.next) {
        const child = buildTree(config, node.next, depth + 1, visited)
        if (child) children.push(child)
      }
      break
  }

  return { id: nodeId, type: node.type, label, depth, children }
}

function TreeItem({
  node,
  selectedId,
  onSelect,
}: {
  node: TreeNode
  selectedId: string | undefined
  onSelect: (id: string) => void
}) {
  const Icon = NODE_ICONS[node.type]
  const isSelected = node.id === selectedId

  return (
    <div>
      <button
        onClick={() => onSelect(node.id)}
        className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-ui-xs transition-colors ${
          isSelected
            ? `border-l-2 border-primary-default bg-primary-alpha-8 text-primary-strong`
            : `text-default hover:bg-surface-subtle`
        }`}
        style={{ paddingLeft: `${node.depth * 16 + 8}px` }}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{node.label}</span>
      </button>
      {node.children.map((child) => (
        <TreeItem key={child.id} node={child} selectedId={selectedId} onSelect={onSelect} />
      ))}
    </div>
  )
}

export function IvrNodeTree({
  config,
  selectedNodeId,
  onSelectNode,
}: {
  config: IvrConfig | undefined
  selectedNodeId: string | undefined
  onSelectNode: (id: string) => void
}) {
  if (!config?.root || !config?.nodes) {
    return <div className="p-4 text-body-xs text-subtle">No IVR configuration yet.</div>
  }

  // If root is a businessHours node, skip it and show the duringHours subtree
  const rootNode = config.nodes[config.root]
  const effectiveRoot = rootNode?.type === `businessHours` ? (rootNode as any).duringHours : config.root

  const tree = buildTree(config, effectiveRoot, 0, new Set())
  if (!tree) return <div className="p-4 text-body-xs text-subtle">Empty configuration.</div>

  return (
    <aside className="w-72 shrink-0 overflow-y-auto border-r border-default bg-surface p-3">
      <p className="mb-2 text-label-sm text-subtle">Call Flow</p>
      <TreeItem node={tree} selectedId={selectedNodeId} onSelect={onSelectNode} />
    </aside>
  )
}
